// The prompts. Every model call shares one cached prefix: the contract document, then the kind
// pack (notes, lessons, the worked example, the skeleton). Only the last message differs
// between the spec step, the generate step and each repair, so the prefix is paid for once per
// kind per cache window. Nothing here calls a model or touches a filesystem: the texts come
// from a PromptSources (sources.node.ts under Node, runtime/client/gensources.ts in the page).

import type { ChatMessage, ContentPart } from "./openrouter.js";
import { KIND_PACKS, type KindPack } from "./kinds.js";
import { lessonsFor } from "./lessons.js";
import { SPEC_SCHEMA, KINDS, STYLES, type Spec } from "./spec.js";
import type { Finding } from "./validate.js";
import { spriteIndex } from "../shared/sprite-index.js";

export interface PromptSources {
  /** runtime/gen/contract.md */
  contract: string;
  /** games/<slug>/game.js for a kind pack's example. */
  example(slug: string): string;
}

// ---- classify ---------------------------------------------------------------------------------

export interface Classification {
  ok: boolean;
  /** Why not, when not ok: a product-facing sentence. */
  reason: string;
  kind: string;
  kind2: string;
  confidence: number;
  title: string;
}

export const CLASSIFY_SCHEMA = {
  name: "classification",
  strict: true,
  schema: {
    type: "object",
    additionalProperties: false,
    required: ["ok", "reason", "kind", "kind2", "confidence", "title"],
    properties: {
      ok: { type: "boolean", description: "False only for a request that is not a game, or one that cannot be made (text chat, drawing, turn-based cards), or that targets a real person." },
      reason: { type: "string", description: "When not ok, one plain sentence for the user. Otherwise empty." },
      kind: { type: "string", enum: [...KINDS] },
      kind2: { type: "string", enum: [...KINDS], description: "The runner-up kind." },
      confidence: { type: "number", minimum: 0, maximum: 1 },
      title: { type: "string", description: "A short working title in the game's own words, two or three words." },
    },
  },
} as const;

export function classifyMessages(prompt: string): ChatMessage[] {
  const kinds = KIND_PACKS.map((k) => `- ${k.id}: ${k.pick}`).join("\n");
  return [
    {
      role: "system",
      content:
        "You sort requests for small multiplayer browser games into one of six kinds. Classify by mechanics only: the camera (top-down or side view), what the pointer does, what A and B do, what the other players are to each other. Ignore the theme: a haunted bakery is a theme, a top-down walk-about with things to pick up is a kind. Every game is drop-in multiplayer with up to 32 strangers, no rounds, no lobby, 20 ticks a second, one target point and two buttons as input.\n\nKinds:\n" +
        kinds +
        "\n\nRefuse (ok = false) only what cannot be made with that input and that runtime: text chat or typing games, drawing, turn-based card games, anything that depends on a real person or a copyrighted property by name. Otherwise ok = true.",
    },
    { role: "user", content: `Request: ${prompt.trim()}` },
  ];
}

// ---- the shared prefix ------------------------------------------------------------------------

/** The two cached parts every spec, generate and repair call starts with. */
export function prefix(pack: KindPack, sources: PromptSources): ChatMessage {
  const lessons = lessonsFor(pack.id);
  const parts: ContentPart[] = [
    { type: "text", text: sources.contract, cache_control: { type: "ephemeral" } },
    {
      type: "text",
      text:
        `# Kind: ${pack.title}\n\nWhen this kind fits: ${pack.pick}\n\n## Notes for this kind\n\n${pack.notes}\n\n` +
        (lessons ? `## Lessons from earlier games of this kind\n\n${lessons}\n\n` : "") +
        `## A finished game of this kind, from the catalogue (games/${pack.example}/game.js)\n\nIt shows the shape, not the content: write your own rules and your own look.\n\n\`\`\`js\n${sources.example(pack.example)}\`\`\`\n\n` +
        `## Starter skeleton\n\nStart from this; keep what fits, replace what does not, finish every TODO.\n\n\`\`\`js\n${pack.skeleton}\`\`\``,
      cache_control: { type: "ephemeral" },
    },
  ];
  return { role: "system", content: parts };
}

// ---- spec -------------------------------------------------------------------------------------

function styleList(): string {
  const byStyle = new Map<string, string[]>();
  for (const p of spriteIndex.packs) byStyle.set(p.style, [...(byStyle.get(p.style) ?? []), p.title]);
  return (
    STYLES.map((s) => (s === "shapes" ? "- shapes: no sprites; everything drawn with canvas shapes in the palette" : `- ${s}: ${(byStyle.get(s) ?? []).join(", ")}`)).join("\n") +
    `\n(${spriteIndex.sprites.length} sprites in all; ask by keyword, one style per game.)`
  );
}

/**
 * The spec is asked for as plain JSON against the schema in the message, not as structured
 * output: Anthropic folds a response schema into the cached prefix, which would make the spec
 * call and the generate call cache different prefixes. checkSpec() validates the reply and the
 * pipeline retries once with the problems listed.
 */
export function specMessages(pack: KindPack, prompt: string, title: string, sources: PromptSources): ChatMessage[] {
  return [
    prefix(pack, sources),
    {
      role: "user",
      content:
        `Design, do not code yet. Produce the spec for this request as one JSON object matching the schema below, and nothing else. Decide the look as part of the design: a style from the sprite library below and a palette for drawn parts. Keep the rules checkable by watching the game. Use the kind "${pack.id}" unless the request plainly needs another.\n\n` +
        `Sprite styles available:\n${styleList()}\n\n` +
        `JSON schema for the spec:\n${JSON.stringify(SPEC_SCHEMA.schema)}\n\n` +
        `Working title: ${title}\nRequest: ${prompt.trim()}`,
    },
  ];
}

// ---- generate ---------------------------------------------------------------------------------

export function generateMessages(pack: KindPack, spec: Spec, sources: PromptSources): ChatMessage[] {
  return [
    prefix(pack, sources),
    {
      role: "user",
      content:
        "Write the complete game.js for this spec. Follow the contract exactly, start from the skeleton, finish every TODO, keep one style of sprites with the shapes fallback, and put the controls line and score field from the spec into meta. Keep the file compact, around 300 to 500 lines: short comments only where a rule is not obvious from the code, no comment banners, no restating of the spec, no helper used once. Output only the JavaScript, no explanation, no fences.\n\n" +
        "Spec:\n" +
        JSON.stringify(spec, null, 2),
    },
  ];
}

// ---- repair -----------------------------------------------------------------------------------

export function repairMessages(pack: KindPack, spec: Spec, source: string, findings: Finding[], round: number, sources: PromptSources): ChatMessage[] {
  const list = findings.map((f) => `- [${f.level}] ${f.code}: ${f.message}`).join("\n");
  return [
    prefix(pack, sources),
    {
      role: "user",
      content:
        `Repair round ${round}. The validator ran the game and found the problems below. Fix every error, keep every rule in the spec (do not delete rules, entities or behaviour to make a check pass), and return the whole corrected game.js. Output only the JavaScript, no explanation, no fences.\n\n` +
        `Findings:\n${list}\n\nSpec:\n${JSON.stringify(spec, null, 2)}\n\nCurrent game.js:\n${source}`,
    },
  ];
}
